import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useSlider as useReactUserSlider } from 'react-use';

type Options = NonNullable<Parameters<typeof useReactUserSlider>[1]>;

interface SliderOptions extends Options {
  initialValue: number;
}

interface SliderHook<TElement> extends ReturnType<typeof useReactUserSlider> {
  ref: React.RefObject<TElement>;
}

// TODO: remove once https://github.com/streamich/react-use/pull/2164 gets merged
export function useSlider<TElement extends HTMLElement>({
  initialValue,
  onScrub,
  onScrubStart,
  ...options
}: SliderOptions): SliderHook<TElement> {
  const ref = useRef<TElement>(null);
  const touched = useRef(false);
  const handleScrubStart = useCallback(() => {
    onScrubStart?.();

    if (touched.current) {
      return;
    }

    touched.current = true;
  }, [onScrubStart]);

  // NOTE: react-use doesn't update callbacks once they're attached to events; it matters when they trap some state
  // value in their scope (usual `useCallback` example) as if the first callback is called, it has bounded values
  // that are not updated
  // TODO: remove once gets merged https://github.com/streamich/react-use/pull/2166
  const scrubCallback = useRef(onScrub);
  useEffect(() => {
    scrubCallback.current = onScrub;
  }, [onScrub]);
  const handleScrub = useCallback((newValue: number) => {
    scrubCallback.current?.(newValue);
  }, []);

  const slider = useReactUserSlider(ref, {
    ...options,
    onScrub: handleScrub,
    onScrubStart: handleScrubStart,
  });

  const value = touched.current ? slider.value : initialValue;

  return {
    ...slider,
    ref,
    value,
  };
}
